import type { Link, Paragraph, Text } from 'mdast';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import { readString } from '../utils';

type SerializableSmartLinkData = {
	'@id'?: unknown;
	name?: unknown;
	title?: unknown;
	url?: unknown;
};

/**
 * Resolve a smart link's URL and display title from its ADF attrs: `url` first, then the
 * resolved JSON-LD `data`. Accepts plain attrs so ADF JSON callers can share the precedence.
 */
export const getSmartLinkParts = (
	attrs: Record<string, unknown> | undefined,
): { title?: string; url?: string } => {
	const data =
		typeof attrs?.data === 'object' && attrs.data !== null && !Array.isArray(attrs.data)
			? (attrs.data as SerializableSmartLinkData)
			: undefined;
	return {
		url: readString(attrs?.url) ?? readString(data?.url) ?? readString(data?.['@id']),
		title: readString(data?.name) ?? readString(data?.title),
	};
};

/** Serialize a smart link as a Markdown link, or its title when it has no URL. */
export function inlineCard(node: PMNode): Link | Text | null {
	const { title, url } = getSmartLinkParts(node.attrs);
	if (!url) {
		return title ? { type: 'text', value: title } : null;
	}

	return {
		type: 'link',
		url,
		title: null,
		children: [{ type: 'text', value: title ?? url }],
	};
}

/** Block and embed cards have no GFM form; emit the same link as an inline card, on its own line. */
export const blockCard = (node: PMNode): Paragraph | null => {
	const link = inlineCard(node);
	return link ? { type: 'paragraph', children: [link] } : null;
};
